import moment from 'moment';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import AutoSizer from 'react-virtualized-auto-sizer';
import VirtualizedList from '../../../../components/VirtualizedList';
import { Spinner } from '../../../../components/Spinner';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEnv } from '../../../../context/env-context';
import { useAuth } from '../../../../hooks/useAuth';
import { makeRequest } from '../../../../utils/makeRequest';
import { toast } from 'react-toastify';
import { AGGREGATOR_FINCH_CONNECTION_BADGE } from '../../../../constants/aggregators';

function AggregatorFinch({ aggregatorId, aggregatorFinchConnections, setAggregator }) {
  const { apiOriginAggregator } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [spinner, setSpinner] = useState(true);
  const [connectionsStatus, setConnectionsStatus] = useState(null);

  const finchRequest = useCallback(
    async ({ url, method = 'GET', payload, onSuccess, successMessage }) => {
      setSpinner(true);
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = {
        token,
        method,
        url: `${apiOriginAggregator}/finch/${url}`,
        [method === 'GET' ? 'params' : 'data']:
          method === 'GET' ? payload : JSON.stringify(payload),
      };

      try {
        const response = await makeRequest(config);

        if (onSuccess) {
          onSuccess(response);
        }

        if (successMessage) {
          toast.success(successMessage);
        }
      } catch (error) {
        toast.error(error);
      } finally {
        setSpinner(false);
      }
    },
    [apiOriginAggregator, getAccessTokenSilently],
  );

  useEffect(() => {
    finchRequest({
      url: 'connections_status',
      payload: { aggregatorId },
      onSuccess: response => setConnectionsStatus(response),
    });
  }, [aggregatorId, finchRequest]);

  const tableColumns = useMemo(
    () => [
      {
        key: 'connectionId',
        header: 'Connection ID',
        width: 180,
        renderValue: val => <div className="text-truncate">{val}</div>,
      },
      {
        key: 'companyName',
        header: 'Company Name',
        width: 200,
      },
      {
        key: 'companyEin',
        header: 'EIN',
        width: 180,
      },
      {
        key: 'companyEntityType',
        header: 'Type',
        width: 120,
      },
      {
        key: 'companyEntitySubtype',
        header: 'Sub Type',
        width: 120,
      },
      {
        key: 'providerName',
        header: 'Provider Name',
        width: 200,
      },
      {
        key: 'reconnect',
        header: '',
        width: 200,
        renderValue: (_, { connectionId }) => (
          <Button
            className="bo-common-inline-button"
            onClick={() =>
              finchRequest({
                url: 'create_reconnect_url',
                payload: { aggregatorId, connectionId },
                successMessage: '“Create Reconnect” URL has been created and copied to clipboard',
                onSuccess: response => navigator.clipboard.writeText(response.connectUrl),
              })
            }
          >
            Create Reconnect URL
          </Button>
        ),
      },
      {
        key: 'disconnect',
        header: '',
        width: 140,
        renderValue: (_, { connectionId }) => (
          <Button
            className="bo-common-inline-button"
            onClick={() =>
              finchRequest({
                url: 'disconnect',
                payload: { aggregatorId, connectionId },
                method: 'POST',
                successMessage: 'Connection has been disconnected',
                onSuccess: () =>
                  setAggregator(prevAgg => ({
                    ...prevAgg,
                    finchConnections: prevAgg.finchConnections.filter(
                      x => x.connectionId !== connectionId,
                    ),
                  })),
              })
            }
          >
            Disconnect
          </Button>
        ),
      },
    ],
    [aggregatorId, finchRequest, setAggregator],
  );

  const rowsAlly = useMemo(
    () =>
      connectionsStatus
        ? {
            key: 'connectionId',
            columns: [
              {
                key: 'status',
                width: 180,
                renderValue: val => (
                  <Badge
                    className={`bo-inline-badge ${AGGREGATOR_FINCH_CONNECTION_BADGE[val]?.color}`}
                  >
                    {AGGREGATOR_FINCH_CONNECTION_BADGE[val]?.text || val}
                  </Badge>
                ),
              },
              {
                key: 'message',
                width: 820,
              },
              {
                key: 'last_successful_sync',
                width: 340,
                renderValue: val => (
                  <div>
                    Last synced: <strong>{val ? moment.utc(val).local().fromNow() : null}</strong>
                  </div>
                ),
              },
            ],
            data: connectionsStatus,
          }
        : null,
    [connectionsStatus],
  );

  return (
    <div className="w-100 bo-h-372">
      <div className="p-3 d-flex gap-2">
        <Button
          className="bo-common-button"
          disabled={spinner}
          onClick={() =>
            finchRequest({
              url: 'create_connect_url',
              payload: { aggregatorId },
              successMessage:
                '“Create New Connection” URL has been created and copied to clipboard',
              onSuccess: response => navigator.clipboard.writeText(response.connectUrl),
            })
          }
        >
          Create new connection URL
        </Button>
        <Button
          className="bo-common-button"
          disabled={spinner || aggregatorFinchConnections.length === 0}
          onClick={() =>
            finchRequest({
              url: 'sync',
              payload: { aggregatorId },
              successMessage: 'Finch sync system job has been launched.',
              method: 'POST',
            })
          }
        >
          Run Sync
        </Button>
      </div>
      <AutoSizer>
        {({ height, width }) => (
          <Spinner visible={spinner} overlayWidth={width}>
            <VirtualizedList
              tableRows={aggregatorFinchConnections}
              rowsAlly={rowsAlly}
              tableColumns={tableColumns}
              width={width}
              height={height - 58}
              rowKey="connectionId"
              headerTopBorder
            />
          </Spinner>
        )}
      </AutoSizer>
    </div>
  );
}

export default AggregatorFinch;
